import { useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import TopBar from "@/components/shell/top-bar";
import Footer from "@/components/shell/footer";

interface DetailLayoutContext {
  setActions: (node: ReactNode) => void;
}

/**
 * Layout for detail routes (/grid/:id, /wind/:id).
 * Sub-header: Back pinned left, action cluster pinned right. Child pages
 * register their action buttons via useDetailActions so everything lives
 * on one row instead of flowing into page content below.
 */
export default function DetailLayout({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [actions, setActions] = useState<ReactNode>(null);

  // Map detail routes to their parent list page
  const parentList = pathname.startsWith("/wind") ? "/wind" : "/grids";

  return (
    <div className="min-h-screen bg-bg">
      <TopBar />

      <div className="flex items-center justify-between gap-2 px-4 py-2 bg-surface border-b border-border">
        <button
          className="inline-flex items-center justify-center h-7 px-3 text-xs font-medium rounded border border-border text-dim hover:text-text hover:border-dim transition-colors"
          onClick={() => navigate(parentList)}
          aria-label="Go back"
        >
          ← Back
        </button>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      <main className="px-4 py-4">
        {children ?? <Outlet context={{ setActions } satisfies DetailLayoutContext} />}
      </main>
      <Footer />
    </div>
  );
}

/**
 * Child page hook: mounts `node` into the parent DetailLayout's sub-header
 * actions slot. Cleared on unmount so stale buttons don't linger when the
 * user navigates away.
 */
export function useDetailActions(node: ReactNode) {
  const ctx = useOutletContext<DetailLayoutContext | undefined>();
  useEffect(() => {
    if (!ctx) return;
    ctx.setActions(node);
    return () => ctx.setActions(null);
  }, [ctx, node]);
}
